import { Appointment } from "@/features/appointments/types/Appointment";
import { useNutritionistConsult } from "@/features/experts/hooks/useNutritionistConsult";
import { useSpecialistConsult } from "@/features/experts/hooks/useSpecialistConsult";
import { useHealthAssessment } from "@/features/health-assessment/hooks/useHealthAssessment";
import { useBloodTracker } from "@/features/holifya-tests/blood-test/hooks/useBloodTracker";
import { useDnaTracker } from "@/features/holifya-tests/dna-test/hooks/useDnaTracker";
import { DnaTestTrackingStatus } from "@/features/holifya-tests/dna-test/types/DnaTestTrackingStatus";
import { useGetDocuments } from "@/features/ocr/hooks/useGetDocuments";
import { BloodTestTrackingStatus } from "@/utils/enums/TrackingStatus";
import { useConsultsStore } from "@/utils/stores/useConsultsStore";
import { useNavigate } from "react-router-dom";

export const useSubHealthDnaWidget = () => {
    const { bloodTracker, updateTracker: updateBloodTracker } = useBloodTracker()
    const { dnaTracker, dnaOrderId, updateTracker: updateDnaTracker } = useDnaTracker()
    const navigate = useNavigate()
    const { isHealthAssessmentCompleted } = useHealthAssessment()
    const healthAssessmentCompleted = isHealthAssessmentCompleted()
    const { data: documents } = useGetDocuments();
    const filesUploaded = documents !== undefined && documents.length !== 0
    const { shouldPushBooking } = useSpecialistConsult()
    const { shouldPushNutritionistBooking, nutriConsultsLeft } = useNutritionistConsult()
    const { consultsData } = useConsultsStore()
    const specialistConsultsLeft = consultsData.specialistConsultsLeft
    const nutritionistConsultsLeft = consultsData.nutritionistConsultsLeft

    type BloodWidgetConfig = {
        text: string;
        buttonText: string;
        onClick: () => void;
        expertRole?: string,
        consult?: Appointment,
        variant?: "default" | "success";
    };

    const earlyBloodStatuses = [
        BloodTestTrackingStatus.Purchased,
        BloodTestTrackingStatus.Delivered,
        BloodTestTrackingStatus.TestDone,
        BloodTestTrackingStatus.SampleRegistered,
    ];

    const earlyDnaStatuses = [
        DnaTestTrackingStatus.Purchased,
        DnaTestTrackingStatus.Delivered,
        DnaTestTrackingStatus.TestDone,
        DnaTestTrackingStatus.SampleRegistered,
        DnaTestTrackingStatus.PickupRequested,
    ];

    const bloodConfigMap: Partial<Record<BloodTestTrackingStatus, BloodWidgetConfig>> = {
        [BloodTestTrackingStatus.Purchased]: {
            text: "📦 Hai ricevuto il kit?",
            buttonText: "Sì, ricevuto",
            onClick: () => updateBloodTracker(BloodTestTrackingStatus.Delivered),
        },
        [BloodTestTrackingStatus.Delivered]: {
            text: "🩸 Effettua l'analisi degli Aminoacidi",
            buttonText: "Effettua",
            onClick: () => navigate("/blood-test/tutorial"),
        },
        [BloodTestTrackingStatus.TestDone]: {
            text: "🔐 Registra il Codice campione",
            buttonText: "Registra",
            onClick: () => navigate("/blood-test/sample-register"),
        },
        [BloodTestTrackingStatus.SampleRegistered]: {
            text: "🚛 Richiedi il ritiro del campione",
            buttonText: "Richiedi",
            onClick: () => navigate("/blood-test/packing"),
        },
    };

    if (earlyBloodStatuses.includes(bloodTracker as BloodTestTrackingStatus)) {
        const config = bloodConfigMap[bloodTracker as BloodTestTrackingStatus];
        if (config) return { ...config };
    }

    const dnaConfigMap: Partial<Record<DnaTestTrackingStatus, BloodWidgetConfig>> = {
        [DnaTestTrackingStatus.Purchased]: {
            text: "📦 Hai ricevuto il kit?",
            buttonText: "Sì, ricevuto",
            onClick: () => updateDnaTracker(DnaTestTrackingStatus.Delivered, dnaOrderId!),
        },
        [DnaTestTrackingStatus.Delivered]: {
            text: "🧬 Effettua il test del DNA",
            buttonText: "Effettua",
            onClick: () => navigate("/dna-test/tutorial"),
        },
        [DnaTestTrackingStatus.TestDone]: {
            text: "🔐 Registra il Codice campione",
            buttonText: "Registra",
            onClick: () => navigate("/dna-test/register-swab-code"),
        },
        [DnaTestTrackingStatus.SampleRegistered]: {
            text: "🚛 Richiedi il ritiro del campione",
            buttonText: "Richiedi",
            onClick: () => navigate("/dna-test/packing"),
        },
        [DnaTestTrackingStatus.PickupRequested]: {
            text: "🚛 Modifica il ritiro del campione",
            buttonText: "Modifica",
            onClick: () => navigate("/dna-test/request-pickup"),
        },
    }

    if (earlyDnaStatuses.includes(dnaTracker as DnaTestTrackingStatus)) {
        const config = dnaConfigMap[dnaTracker as DnaTestTrackingStatus];
        if (config) return { ...config };
    }

    if (!healthAssessmentCompleted) {
        return {
            text: "📝 Rispondi all'Health Assessment",
            buttonText: "Rispondi",
            onClick: () => { navigate("/health-assessment-home-page") },
        }
    }

    if (!filesUploaded) {
        return {
            text: "📄 Carica i tuoi referti",
            buttonText: "Carica",
            onClick: () => { navigate("/reports") },
        }
    }

    if (bloodTracker && bloodTracker === BloodTestTrackingStatus.PickupRequested) {
        return {
            text: "🚛 Modifica il ritiro del campione",
            buttonText: "Modifica",
            onClick: () => navigate("/blood-test/request-pickup"),
        }
    }

    if (bloodTracker && bloodTracker === BloodTestTrackingStatus.DeliveredToLab) {
        return {
            text: "🔬 In analisi",
            buttonText: "",
            onClick: () => { },
        }
    }

    if (dnaTracker && dnaTracker === DnaTestTrackingStatus.PickupRequested) {
        return {
            text: "🚛 Modifica il ritiro del campione",
            buttonText: "Modifica",
            onClick: () => navigate("/dna-test/request-pickup"),
        }
    }

    if (dnaTracker && dnaTracker === DnaTestTrackingStatus.Departed) {
        return {
            text: "🚛 Verso il laboratorio",
            buttonText: "",
            onClick: () => { },
        }
    }

    if (dnaTracker && dnaTracker === DnaTestTrackingStatus.DeliveredToLaboratory) {
        return {
            text: "📍 Arrivato in laboratorio",
            buttonText: "",
            onClick: () => { },
        }
    }

    if (dnaTracker && dnaTracker === DnaTestTrackingStatus.StartingToExtract) {
        return {
            text: "🧪 Inizio estrazione",
            buttonText: "",
            onClick: () => { },
        }
    }

    if (dnaTracker && dnaTracker === DnaTestTrackingStatus.StartingAnalysis) {
        return {
            text: "🔬 In analisi",
            buttonText: "",
            onClick: () => { },
        }
    }

    if (dnaTracker && dnaTracker === DnaTestTrackingStatus.AnalysisDone) {
        return {
            text: "🧬 Risultati in arrivo",
            buttonText: "",
            onClick: () => { },
        }
    }

    const defaultReturn = shouldPushBooking && specialistConsultsLeft && specialistConsultsLeft > 0 ? {
        text: "👩‍⚕️️ Prenota consulto con medico",
        buttonText: "Prenota",
        onClick: () => { navigate("/equipe/your-experts") },
    } : shouldPushNutritionistBooking && nutritionistConsultsLeft && nutritionistConsultsLeft > 0 ? {
        text: "👨‍⚕️️ Prenota consulto con nutrizionista",
        buttonText: "Prenota",
        onClick: () => { navigate("/equipe/your-experts") },
    } : nutriConsultsLeft.length > 0 ? {
        text: `${nutriConsultsLeft[0].expert.data.name} ${nutriConsultsLeft[0].expert.data.family_name}`,
        buttonText: "Connettiti",
        expertRole: nutriConsultsLeft[0].expert.role,
        consult: nutriConsultsLeft[0],
        onClick: () => { window.open(nutriConsultsLeft[0].connectUrl, "_blank") },
    } : {
        text: "📝 Rispondi al check-up",
        buttonText: "Rispondi",
        onClick: () => { navigate("/check-up-assessment-home-page") },
    }


    return defaultReturn

}